import { createContext, useContext, useState, type ReactNode } from 'react'
import { createAppKit } from '@reown/appkit'
import { WagmiAdapter } from '@reown/appkit-adapter-wagmi'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { WagmiProvider, useAccount, useWalletClient, useChainId, type Config } from 'wagmi'
import { pulsechain, mainnet } from 'viem/chains'
import type { AppKitNetwork } from '@reown/appkit/networks'
import type { Chain } from 'viem'

// Get Reown (WalletConnect) project ID from environment
// If not set, wallet modal will show an error but app will still work
const projectId = import.meta.env.VITE_REOWN_PROJECT_ID || ''

if (!projectId) {
  console.warn('VITE_REOWN_PROJECT_ID not set. Get a free project ID at https://cloud.reown.com')
}

const queryClient = new QueryClient()

const chains = [pulsechain as Chain, mainnet as Chain]
const networks = chains as unknown as [AppKitNetwork, ...AppKitNetwork[]]

// Create Wagmi adapter
const wagmiAdapter = new WagmiAdapter({
  projectId,
  networks,
})

export const wagmiConfig = wagmiAdapter.wagmiConfig

// Create the modal
const modal = createAppKit({
  adapters: [wagmiAdapter],
  projectId,
  networks,
  features: {
    analytics: false,
  },
})

interface Web3ContextType {
  address: `0x${string}` | undefined
  isConnected: boolean
  isWrongChain: boolean
  desiredChainId: number
  setDesiredChainId: (chainId: number) => void
  openModal: () => void
  switchToPulsechain: () => void
  walletClient: any
}

const Web3Context = createContext<Web3ContextType | null>(null)

export function Web3Provider({ children }: { children: ReactNode }) {
  const account = useAccount()
  const { data: walletClient } = useWalletClient()
  const chainId = useChainId()
  const [desiredChainId, setDesiredChainId] = useState(369) // PulseChain

  const openModal = () => {
    modal.open()
  }

  const switchToPulsechain = () => {
    modal.switchNetwork(pulsechain as unknown as AppKitNetwork)
  }

  const isWrongChain = chainId !== desiredChainId
  const isConnected = account.isConnected

  return (
    <Web3Context.Provider
      value={{
        address: account.address,
        isConnected,
        isWrongChain,
        desiredChainId,
        setDesiredChainId,
        openModal,
        switchToPulsechain,
        walletClient,
      }}
    >
      {children}
    </Web3Context.Provider>
  )
}

export function Web3ProviderWrapper({ children }: { children: ReactNode }) {
  return (
    <WagmiProvider config={wagmiConfig as Config}>
      <QueryClientProvider client={queryClient}>
        <Web3Provider>{children}</Web3Provider>
      </QueryClientProvider>
    </WagmiProvider>
  )
}

export function useWeb3() {
  const context = useContext(Web3Context)
  if (!context) {
    throw new Error('useWeb3 must be used within Web3Provider')
  }
  return context
}

